// PR 2384's generated client is isolated behind an alias until its proto changes
// are available in the main dev-plane package. This preserves newer APIs already
// consumed by the UI while allowing this adapter to use the pending policy fields.
import { CloudPolicyService } from "@cloudpolicy-devplane/connectrpc_es/devplaneapi/v1/cloud_policy_connect";
import type { Client } from "@connectrpc/connect";
import {
  DefaultRule,
  type CloudPolicy,
  type CloudQuota,
  type CloudQuotaUtilization,
} from "@cloudpolicy-devplane/bufbuild_es/devplaneapi/v1/cloud_policy_pb";
import { createConnectClient } from "./connect";

type BaseRes = {
  success?: boolean;
  message?: string;
};

type LegacyCloudPolicyRequest = {
  cloud_cred_id?: string;
  organization_id?: string;
  default_rule?: string;
  override_system_policy?: boolean;
  quotas?: Array<{
    instance_type?: string;
    gpu_type?: string;
    limit?: number;
  }>;
  context?: {
    name?: string;
    description?: string;
    tags?: string[];
  };
};

type LegacyListCloudPolicyRequest = {
  page_params?: {
    page_size?: number;
    page_token?: string;
  };
  options?: {
    organization_id?: string;
    cloud_cred_id?: string;
    include_quota_current?: boolean;
  };
};

type LegacyUpdateCloudPolicyDefaultRuleRequest = {
  id?: string;
  default_rule?: string;
};

type LegacyUpdateCloudPolicySystemPolicyOverrideRequest = {
  id?: string;
  override_system_policy: boolean;
};

type LegacyAddQuotasRequest = {
  id?: string;
  quotas?: LegacyCloudPolicyRequest["quotas"];
};

type LegacyUpdateQuotaRequest = {
  quota_id?: string;
  instance_type?: string;
  gpu_type?: string;
  limit?: number;
};

type DevplaneLegacyAdapterClients = {
  cloudPolicy: Client<typeof CloudPolicyService>;
};

const clients: DevplaneLegacyAdapterClients = {
  cloudPolicy: createConnectClient(CloudPolicyService),
};

type DevplaneLegacyAdapterClientOverrides = {
  [K in keyof DevplaneLegacyAdapterClients]?: Partial<
    DevplaneLegacyAdapterClients[K]
  >;
};

export function createDevplaneLegacyAdapter(
  overrides: DevplaneLegacyAdapterClientOverrides = {},
) {
  const c = {
    cloudPolicy: { ...clients.cloudPolicy, ...overrides.cloudPolicy },
  };
  return {
    listCloudPolicies: async (
      orgId: string,
      request?: LegacyListCloudPolicyRequest,
    ) => {
      const response = await c.cloudPolicy.listCloudPolicy(
        mapListCloudPolicyRequest(orgId, request),
      );
      return {
        ...ok(response.items.map(mapCloudPolicy)),
        system_policies: (response.systemPolicies || []).map(mapCloudPolicy),
        next_page_token: response.nextPageToken || undefined,
      };
    },
    createCloudPolicy: async (
      orgId: string,
      request: LegacyCloudPolicyRequest,
    ) =>
      ok(
        mapCloudPolicy(
          (
            await c.cloudPolicy.createCloudPolicy(
              mapCreateCloudPolicyRequest(orgId, request),
            )
          ).cloudPolicy,
        ),
      ),
    getCloudPolicy: async (_orgId: string, policyId: string) =>
      ok(
        mapCloudPolicy(
          (await c.cloudPolicy.getCloudPolicy({ id: policyId })).cloudPolicy,
        ),
      ),
    deleteCloudPolicy: async (_orgId: string, policyId: string) => {
      await c.cloudPolicy.deleteCloudPolicy({ id: policyId });
      return ok(undefined);
    },
    updateCloudPolicyDefaultRule: async (
      _orgId: string,
      policyId: string,
      request: LegacyUpdateCloudPolicyDefaultRuleRequest,
    ) =>
      ok({
        cloud_policy: mapCloudPolicy(
          (
            await c.cloudPolicy.updateCloudPolicyDefaultRule({
              id: request.id || policyId,
              defaultRule: mapDefaultRule(request.default_rule),
            })
          ).cloudPolicy,
        ),
      }),
    updateCloudPolicySystemPolicyOverride: async (
      _orgId: string,
      policyId: string,
      request: LegacyUpdateCloudPolicySystemPolicyOverrideRequest,
    ) =>
      ok({
        cloud_policy: mapCloudPolicy(
          (
            await c.cloudPolicy.updateCloudPolicySystemPolicyOverride({
              id: request.id || policyId,
              overrideSystemPolicy: request.override_system_policy,
            })
          ).cloudPolicy,
        ),
      }),
    getCloudPolicyUtilization: async (orgId: string, policyId: string) =>
      ok(
        (
          await c.cloudPolicy.getCloudPolicyUtilization({
            policyId,
            organizationId: orgId,
          })
        ).quotaUtilizations.map(mapQuotaUtilization),
      ),
    addQuotasToCloudPolicy: async (
      _orgId: string,
      policyId: string,
      request: LegacyAddQuotasRequest,
    ) =>
      ok(
        mapCloudPolicy(
          (
            await c.cloudPolicy.addQuotasToCloudPolicy({
              id: request.id || policyId,
              quotas: (request.quotas || []).map(mapCreateQuota),
            })
          ).cloudPolicy,
        ),
      ),
    updateCloudQuota: async (
      _orgId: string,
      _policyId: string,
      quotaId: string,
      request: LegacyUpdateQuotaRequest,
    ) =>
      ok({
        quota: mapQuota(
          (
            await c.cloudPolicy.updateCloudQuota({
              quotaId: request.quota_id || quotaId,
              instanceType: request.instance_type,
              gpuType: request.gpu_type,
              limit: request.limit,
            })
          ).quota,
        ),
      }),
    deleteCloudQuota: async (
      _orgId: string,
      _policyId: string,
      quotaId: string,
    ) => {
      await c.cloudPolicy.deleteCloudQuota({ quotaId });
      return ok(undefined);
    },
    getCloudQuotaUtilization: async (
      orgId: string,
      _policyId: string,
      quotaId: string,
    ) =>
      ok({
        quota_utilization: mapQuotaUtilization(
          (
            await c.cloudPolicy.getCloudQuotaUtilization({
              quotaId,
              organizationId: orgId,
            })
          ).quotaUtilization,
        ),
      }),
  };
}

export const DevplaneLegacyAdapter = createDevplaneLegacyAdapter();

export const ok = <T>(data: T): BaseRes & { data: T } => ({
  success: true,
  data,
});

export const mapCloudPolicy = (policy?: Partial<CloudPolicy>) => ({
  id: policy?.id,
  created_at: policy?.createTime?.toDate().toISOString(),
  updated_at: policy?.updateTime?.toDate().toISOString(),
  cloud_cred_id: policy?.cloudCredId,
  organization_id: policy?.organizationId,
  default_rule: policy?.defaultRule,
  quotas: (policy?.quotas || []).map(mapQuota),
  context: policy?.context
    ? {
        name: policy.context.name,
        description: policy.context.description,
        tags: policy.context.tags,
      }
    : undefined,
  compute_selector: policy?.computeSelector
    ? {
        cloud_provider_ids: policy.computeSelector.cloudProviderIds,
        instance_types: policy.computeSelector.instanceTypes,
        gpu_types: policy.computeSelector.gpuTypes,
        include_cpu: policy.computeSelector.includeCpu,
      }
    : undefined,
  principal_selector: policy?.principalSelector
    ? {
        user_ids: policy.principalSelector.userIds,
        email_domains: policy.principalSelector.emailDomains,
        kinds: policy.principalSelector.kinds,
        principal_ids: policy.principalSelector.principalIds,
      }
    : undefined,
  source: policy?.source,
  denial_code: policy?.denialCode,
  denial_message: policy?.denialMessage,
  override_system_policy: policy?.overrideSystemPolicy,
});

const mapQuota = (quota?: Partial<CloudQuota>) => ({
  id: quota?.id,
  instance_type: quota?.instanceType,
  gpu_type: quota?.gpuType,
  limit: quota?.limit,
});

const mapQuotaUtilization = (utilization?: Partial<CloudQuotaUtilization>) => ({
  quota_id: utilization?.quotaId,
  current: utilization?.current,
});

const mapListCloudPolicyRequest = (
  orgId: string,
  request?: LegacyListCloudPolicyRequest,
) => ({
  pageParams: request?.page_params
    ? {
        pageSize: request.page_params.page_size,
        pageToken: request.page_params.page_token,
      }
    : undefined,
  options: {
    organizationId: request?.options?.organization_id || orgId,
    cloudCredId: request?.options?.cloud_cred_id,
    includeQuotaCurrent: request?.options?.include_quota_current,
  },
});

const mapCreateCloudPolicyRequest = (
  orgId: string,
  request: LegacyCloudPolicyRequest,
) => ({
  cloudCredId: request.cloud_cred_id,
  organizationId: request.organization_id || orgId,
  defaultRule: mapDefaultRule(request.default_rule),
  quotas: (request.quotas || []).map(mapCreateQuota),
  context: request.context,
  overrideSystemPolicy: request.override_system_policy,
});

const mapCreateQuota = (
  quota: NonNullable<LegacyCloudPolicyRequest["quotas"]>[number],
) => ({
  instanceType: quota.instance_type,
  gpuType: quota.gpu_type,
  limit: quota.limit,
});

const mapDefaultRule = (rule?: string) => {
  if (rule === "DEFAULT_RULE_DENY") {
    return DefaultRule.DENY;
  }
  if (rule === "DEFAULT_RULE_UNSPECIFIED") {
    return DefaultRule.UNSPECIFIED;
  }
  return DefaultRule.ALLOW;
};
